/**
 * A support ticket's lifecycle — inbound calls and texts, tracked the same
 * way a repair ticket is. Shaped like the other machines for the same
 * reason: one pattern for "what can this record legally become next"
 * everywhere it appears.
 *
 * `open` needs staff attention (a new inquiry, or one a customer just
 * replied to). `pending` is staff waiting on the customer. `resolved` is
 * staff believing the matter is done but not yet closing it. `closed` is
 * final — but unlike a repair ticket, closing here is not the end: a new
 * inbound message on a closed ticket's number opens a fresh one, and a
 * closed ticket itself can still be reopened by hand.
 */
export const SUPPORT_TICKET_STATUSES = ['open', 'pending', 'resolved', 'closed'];
export const SUPPORT_TICKET_TRANSITIONS = {
    open: ['pending', 'resolved', 'closed'],
    pending: ['open', 'resolved', 'closed'],
    resolved: ['open', 'closed'],
    closed: ['open'],
};
export function canTransitionSupportTicket(from, to) {
    return SUPPORT_TICKET_TRANSITIONS[from].includes(to);
}
/** How a ticket started. `web_chat` (t0948), `contact_form` (t1075) and
 *  `email` (t1092) are appended rather than inserted alongside `sms`/`call`
 *  so the generated migration is a plain `ALTER TYPE ... ADD VALUE` with no
 *  `BEFORE` clause. */
export const SUPPORT_CHANNELS = [
    'sms',
    'call',
    'manual',
    'web_chat',
    'contact_form',
    'email',
];
/** The channels staff can record when they open a ticket by hand (t1372).
 *  Web chat and contact form are left out: those are intake paths of their own
 *  (a web chat ticket replies into a widget a hand-made ticket never has). */
export const MANUAL_TICKET_CHANNELS = [
    'manual',
    'call',
    'sms',
    'email',
];
/** What one entry in a ticket's conversation thread is. The `chat_*` values
 *  (t0948) are the storefront chat widget's equivalents of the `sms_*` pair:
 *  `chat_inbound` is a shopper message, `chat_outbound` a staff reply, and
 *  `chat_ai` is the one-time frozen transcript of the pre-handoff AI phase
 *  (see `formatChatTranscriptBody`) — unlike `ai_receptionist`, it never
 *  updates after it's written, so it needs no joined `aiCall` data. */
export const SUPPORT_MESSAGE_TYPES = [
    'sms_inbound',
    'sms_outbound',
    'call_inbound',
    'call_outbound',
    'voicemail',
    'note',
    'ai_receptionist',
    'chat_inbound',
    'chat_outbound',
    'chat_ai',
    'contact_form',
    'email_inbound',
    'email_outbound',
];
/** Why an outbound ticket message went out without a person writing it
 *  (t1236): `after_hours` is the closed-office auto-reply, `faq` an automatic
 *  answer from the receptionist Q&A list. */
export const SUPPORT_AUTO_REPLY_KINDS = ['after_hours', 'faq'];
/** Delivery state of an `email_outbound` ticket message, taken from its mail row. */
export const SUPPORT_EMAIL_DELIVERY_STATUSES = ['queued', 'sent', 'failed'];
/** What happens to mail arriving in the designated support mailbox (t1092).
 *  `off` leaves it in the Mail client for staff to convert by hand; `all`
 *  raises a ticket for every inbound message; `ai` has the AI text generator
 *  screen out spam, newsletters and auto-replies first. Naming a mailbox is
 *  also required — the mode alone does not activate intake. */
export const SUPPORT_EMAIL_INTAKE_MODES = ['off', 'all', 'ai'];
/** A storefront chat session's own lifecycle (t0948) — separate from the
 *  ticket it eventually creates, since most sessions never become a ticket at
 *  all (the shopper gets what they need from the AI and closes the tab).
 *  `ai` is the pre-handoff phase; `agent` and `offline_message` both mean a
 *  ticket exists (see `supportChatSessions.ticketId`); `closed` is terminal —
 *  a fresh chat starts a new session rather than reopening this one. */
export const SUPPORT_CHAT_SESSION_STATUSES = ['ai', 'agent', 'offline_message', 'closed'];
/** Admin-controlled web chat availability (t0948): `auto` follows the same
 *  `businessHours`/`isWithinBusinessHours` the phone line already uses,
 *  `online`/`offline` override it unconditionally. */
export const SUPPORT_WEB_CHAT_AVAILABILITY_MODES = ['auto', 'online', 'offline'];
/** How many shopper turns the chat assistant (t0948) will take before it
 *  forces `suggestAgent: true` — the chat equivalent of `SUPPORT_AI_MAX_TURNS`,
 *  set higher because a typed conversation naturally runs longer than a phone
 *  call. */
export const SUPPORT_CHAT_AI_MAX_TURNS = 12;
/** A ring target either dials an outside phone number, or rings a signed-in
 *  staff member's browser softphone (t0869) — see `supportCallTargetSchema`. */
export const SUPPORT_CALL_TARGET_KINDS = ['phone', 'client'];
/** When the keypad phone menu (t1100) plays: on every inbound call, or only
 *  once the call would otherwise skip the ring list (outside business hours). */
/** How often ticket texts carry the brand and "Reply STOP" wording (t1233). */
export const SUPPORT_SMS_WORDING_MODES = ['every_text', 'first_and_periodic'];
/** t1405: the voices the shop can pick for everything the phone line says.
 *  `id` is the Twilio `<Say voice>` name; 'default' is Twilio's basic voice
 *  (a bare `<Say>`), kept as the starting point so nothing changes until a
 *  voice is chosen. */
export const SUPPORT_PHONE_VOICES = [
    { id: 'default', label: 'Basic (current)', description: 'Twilio’s standard robotic voice' },
    { id: 'Polly.Joanna-Neural', label: 'Joanna', description: 'Female, clear and friendly' },
    { id: 'Polly.Matthew-Neural', label: 'Matthew', description: 'Male, clear and friendly' },
    { id: 'Polly.Ruth-Generative', label: 'Ruth', description: 'Female, most natural sounding' },
    { id: 'Polly.Stephen-Generative', label: 'Stephen', description: 'Male, most natural sounding' },
    {
        id: 'Google.en-US-Chirp3-HD-Aoede',
        label: 'Aoede',
        description: 'Female, warm and conversational',
    },
    {
        id: 'Google.en-US-Chirp3-HD-Charon',
        label: 'Charon',
        description: 'Male, warm and conversational',
    },
];
export const SUPPORT_PHONE_VOICE_IDS = SUPPORT_PHONE_VOICES.map((v) => v.id);
export const SUPPORT_IVR_MODES = ['always', 'after_hours'];
/** What a menu key does: `message` speaks a canned reply (hours, location,
 *  holiday notice) and replays the menu; `route` hands the call to the normal
 *  ring list / AI receptionist / voicemail flow. */
export const SUPPORT_IVR_ACTIONS = ['message', 'route'];
/** Strings, not numbers: Twilio posts `Digits` as text and `'0'` is a real key. */
export const SUPPORT_IVR_DIGITS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
/** How many times one call hears the menu (first play, replays after a
 *  message, invalid or missing key) before it falls through to the normal
 *  routing — a hard ceiling so a caller can never loop forever. */
export const SUPPORT_IVR_MAX_PROMPTS = 4;
/** How many caller turns the AI receptionist (t0889) will take before it
 *  forces a handoff to voicemail — a hard ceiling so a confused caller (or a
 *  confused model) can never ring up cost or dead air indefinitely. */
export const SUPPORT_AI_MAX_TURNS = 6;
/** `active` while the call is in progress; `voicemail`/`resolved` once the AI
 *  hands off or the caller says goodbye; `abandoned` is reserved for a call
 *  that never reaches either — not currently set anywhere (nothing marks a
 *  hung-up-mid-conversation call), but kept in the enum so it can be without
 *  a migration. */
export const SUPPORT_AI_CALL_STATUSES = ['active', 'voicemail', 'resolved', 'abandoned'];
/** Lifecycle of the auto-transcript on an answered browser-softphone call's
 *  recording (t0886). `null` on a message row means no transcript is
 *  expected at all — voicemail, SMS, and note entries never set this.
 *  `pending` while the transcription job is queued or running, `skipped`
 *  when no transcription provider is configured (the recording still plays),
 *  `failed` once retries are exhausted. */
export const SUPPORT_TRANSCRIPT_STATUSES = ['pending', 'completed', 'failed', 'skipped'];
/** Picture messages (MMS) on ticket texts (t1231). These are the formats
 *  Twilio will transcode for carriers; anything else (HEIC, PDF) is refused
 *  up front rather than silently dropped by the carrier. The byte cap is
 *  Twilio's whole-message limit, so it applies to the sum of a reply's
 *  pictures, not to each one. */
export const SMS_MEDIA_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/gif'];
export const MAX_SMS_MEDIA_BYTES = 5 * 1024 * 1024;
export const MAX_SMS_MEDIA_COUNT = 5;
/** A recording the phone menu plays instead of speaking its greeting (t1401).
 *  MP3 and WAV are what Twilio's <Play> handles and what people actually have. */
export const PHONE_AUDIO_CONTENT_TYPES = ['audio/mpeg', 'audio/wav'];
export const MAX_PHONE_AUDIO_BYTES = 10 * 1024 * 1024;
//# sourceMappingURL=support-states.js.map